import { axiosPatch } from '@/api/handlers';
import { useMutation } from '@/api/hooks';
import type { FormControlProp } from '@/components';
import { useInvalidateQuery } from '@/hooks';
import { logger } from '@/utils';
import Big from 'big.js';
import { useWatch } from 'react-hook-form';
import type { z } from 'zod';
import { queryKeyMap } from '../../../../_utils/query-keys';
import {
  FundAdditionalIncomeAndExpenditurePriorMutationSchema,
  FundAdditionalIncomeAndExpenditureSchema,
  type PeriodScopeType,
} from '../../../_models/fund-additiona-income-and-expenditure';

type WatchProps = {
  control: FormControlProp<
    z.infer<typeof FundAdditionalIncomeAndExpenditureSchema>
  >;
  type: PeriodScopeType;
};

export const useIncomeSummaryByTypeWatch = ({
  control,
  type,
}: WatchProps): Big | null => {
  const previousYearPerformanceValues = useWatch({
    control,
    name: [
      'grantedAmountByTheCountry',
      'investmentIncome',
      'businessIncome',
      'otherIncomeAmount',
    ],
  });
  const estimatedThisYearValues = useWatch({
    control,
    name: [
      'estimatedGrantedAmountByTheCountry',
      'estimatedInvestmentIncome',
      'estimatedBusinessIncome',
      'estimatedOtherIncomeAmount',
    ],
  });
  const estimatedNextYearValues = useWatch({
    control,
    name: [
      'estimatedGrantedAmountByTheCountryNextYear',
      'estimatedInvestmentIncomeNextYear',
      'estimatedBusinessIncomeNextYear',
      'estimatedOtherIncomeAmountNextYear',
    ],
  });

  const values =
    type === 'previous-year-performance'
      ? previousYearPerformanceValues
      : type === 'estimated-this-year'
        ? estimatedThisYearValues
        : estimatedNextYearValues;

  if (values.every((v) => v === null || v === undefined)) {
    return null;
  }

  const value = values.reduce((acc, v) => acc.plus(v ?? 0), new Big(0));

  return value;
};

export const useExpenditureSummaryByTypeWatch = ({
  control,
  type,
}: WatchProps): Big | null => {
  const previousYearPerformanceValues = useWatch({
    control,
    name: ['businessExpense', 'administrativeExpense'],
  });

  const estimatedThisYearValues = useWatch({
    control,
    name: ['estimatedBusinessExpense', 'estimatedAdministrativeExpense'],
  });

  const estimatedNextYearValues = useWatch({
    control,
    name: [
      'estimatedBusinessExpenseNextYear',
      'estimatedAdministrativeExpenseNextYear',
    ],
  });

  const values =
    type === 'previous-year-performance'
      ? previousYearPerformanceValues
      : type === 'estimated-this-year'
        ? estimatedThisYearValues
        : estimatedNextYearValues;

  if (values.every((v) => v === null || v === undefined)) {
    return null;
  }

  const value = values.reduce((acc, v) => acc.plus(v ?? 0), new Big(0));

  return value;
};

export const useIncomeAndExpenditureSummaryByTypeWatch = ({
  type,
  control,
}: WatchProps): Big | null => {
  const incomeSummaryByType = useIncomeSummaryByTypeWatch({ type, control });
  const expenditureSummaryByType = useExpenditureSummaryByTypeWatch({
    type,
    control,
  });
  const amountReturnedToTheOrganization = useWatch({
    control,
    name: 'amountReturnedToTheOrganization',
  });
  const estimatedAmountReturnedToTheOrganization = useWatch({
    control,
    name: 'estimatedAmountReturnedToTheOrganization',
  });
  const estimatedAmountReturnedToTheOrganizationNextYear = useWatch({
    control,
    name: 'estimatedAmountReturnedToTheOrganizationNextYear',
  });
  const beginningOfTheFundBalance = useWatch({
    control,
    name: 'beginningOfTheFundBalance',
  });
  const amountReturnedToTheNationalTreasury = useWatch({
    control,
    name: 'amountReturnedToTheNationalTreasury',
  });
  const estimatedAmountReturnedToTheNationalTreasury = useWatch({
    control,
    name: 'estimatedAmountReturnedToTheNationalTreasury',
  });
  const beginningOfTheEstimatedFundBalanceNextYear = useWatch({
    control,
    name: 'beginningOfTheEstimatedFundBalanceNextYear',
  });
  const estimatedAmountReturnedToTheNationalTreasuryNextYear = useWatch({
    control,
    name: 'estimatedAmountReturnedToTheNationalTreasuryNextYear',
  });

  switch (type) {
    case 'previous-year-performance': {
      const targets = [
        beginningOfTheFundBalance,
        incomeSummaryByType,
        expenditureSummaryByType,
        amountReturnedToTheNationalTreasury,
        amountReturnedToTheOrganization,
      ];

      if (targets.every((v) => v === null || v === undefined)) {
        return null;
      }

      return new Big(0)
        .plus(beginningOfTheFundBalance ?? 0)
        .plus(incomeSummaryByType ?? 0)
        .minus(expenditureSummaryByType ?? 0)
        .minus(amountReturnedToTheNationalTreasury ?? 0)
        .minus(amountReturnedToTheOrganization ?? 0);
    }
    case 'estimated-this-year': {
      const targets = [
        beginningOfTheFundBalance,
        incomeSummaryByType,
        expenditureSummaryByType,
        estimatedAmountReturnedToTheNationalTreasury,
        estimatedAmountReturnedToTheOrganization,
      ];

      if (targets.every((v) => v === null || v === undefined)) {
        return null;
      }

      return new Big(0)
        .plus(beginningOfTheFundBalance ?? 0)
        .plus(incomeSummaryByType ?? 0)
        .minus(expenditureSummaryByType ?? 0)
        .minus(estimatedAmountReturnedToTheNationalTreasury ?? 0)
        .minus(estimatedAmountReturnedToTheOrganization ?? 0);
    }
    case 'estimated-next-year': {
      const targets = [
        beginningOfTheEstimatedFundBalanceNextYear,
        incomeSummaryByType,
        expenditureSummaryByType,
        estimatedAmountReturnedToTheNationalTreasuryNextYear,
        estimatedAmountReturnedToTheOrganizationNextYear,
      ];

      if (targets.every((v) => v === null || v === undefined)) {
        return null;
      }

      return new Big(0)
        .plus(beginningOfTheEstimatedFundBalanceNextYear ?? 0)
        .plus(incomeSummaryByType ?? 0)
        .minus(expenditureSummaryByType ?? 0)
        .minus(estimatedAmountReturnedToTheNationalTreasuryNextYear ?? 0)
        .minus(estimatedAmountReturnedToTheOrganizationNextYear ?? 0);
    }
    default: {
      const check: never = type;
      logger.error('Invalid type', check);
      return null;
    }
  }
};

export const useClericalExpenditureSummaryByTypeWatch = ({
  control,
  type,
}: WatchProps): Big | null => {
  const previousYearPerformanceValues = useWatch({
    control,
    name: ['choreExpensesNotFromThisFund', 'personnelExpensesNotFromThisFund'],
  });
  const estimatedThisYearValues = useWatch({
    control,
    name: [
      'estimatedChoreExpensesNotFromThisFund',
      'estimatedPersonnelExpensesNotFromThisFund',
    ],
  });
  const expensesNotFromThisFundNextYearValues = useWatch({
    control,
    name: [
      'estimatedChoreExpensesNotFromThisFundNextYear',
      'estimatedPersonnelExpensesNotFromThisFundNextYear',
    ],
  });

  const values =
    type === 'previous-year-performance'
      ? previousYearPerformanceValues
      : type === 'estimated-this-year'
        ? estimatedThisYearValues
        : expensesNotFromThisFundNextYearValues;

  if (values.every((v) => v === null || v === undefined)) {
    return null;
  }

  const value = values.reduce((acc, v) => acc.plus(v ?? 0), new Big(0));

  return value;
};

export const useUpdateFundAdditionalIncomeAndExpenditure = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      projectId,
      fundAdditionalIncomeAndExpenditure,
    }: {
      projectId: string;
      fundAdditionalIncomeAndExpenditure: z.infer<
        typeof FundAdditionalIncomeAndExpenditureSchema
      >;
    }) => {
      const parsed = FundAdditionalIncomeAndExpenditureSchema.parse(
        fundAdditionalIncomeAndExpenditure,
      );
      const payload = {
        fundAdditionalIncomeAndExpenditure: {
          ...parsed,
        },
      };

      const res = await axiosPatch(`/api/projects/${projectId}/`, payload);
      await Promise.all([
        invalidate(queryKeyMap.project()),
        invalidate(queryKeyMap.projects()),
      ]);
      return res;
    },
  });
};

export const useUpdateFundAdditionalIncomeAndExpenditurePrior = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      fundAdditionalIncomeAndExpenditure,
    }: {
      fundAdditionalIncomeAndExpenditure: z.infer<
        typeof FundAdditionalIncomeAndExpenditurePriorMutationSchema
      >;
    }) => {
      const { id, ...payload } =
        FundAdditionalIncomeAndExpenditurePriorMutationSchema.parse(
          fundAdditionalIncomeAndExpenditure,
        );

      const res = await axiosPatch(
        `/api/projects/fund-additional-income-and-expenditure/${id}/`,
        payload,
      );
      await Promise.all([
        invalidate(queryKeyMap.project()),
        invalidate(queryKeyMap.projects()),
      ]);
      return res;
    },
  });
};
